// Overview: who these books are, and where each month stands. The ladder reads ONLY persisted
// truth (plan §3.1): each period the lifecycle knows, at the status it persists, grouped by the
// fiscal year the engine names. A fiscal year whose every month is sealed folds to one line; nothing
// else is hidden. No per-check detail is shown: the engine persists none.

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardBody, CardHeader, Icon } from '@givmo/design-system';
import type { BooksHome, BooksPeriod } from './books-wire';
import type { BooksWords } from './books-words';
import { BooksPageShell, StatusLabel } from './books-ui';
import { BooksLink } from './books-links';
import { fiscalYearEndWords, periodName, spanName } from './books-format';

const SEALED = 'locked';

export function HomePage({ home, words }: { home: BooksHome; words: BooksWords }) {
  const identity = home.identity;
  const years = groupByFiscalYear(home.periods);
  // A legend only for the statuses present that need one (an open or sealed month speaks for itself).
  const explain = [...new Set(home.periods.map((period) => period.status))].filter(
    (status) => status !== 'open' && status !== SEALED,
  );
  const firstRow = home.statements[0];
  const monthScope = firstRow?.scopes.find((scope) => scope === 'month' || scope === 'as_of');

  return (
    <BooksPageShell title="Overview" testId="books-home">
      <Card testId="books-identity">
        <CardBody className="flex flex-col gap-16 px-24 py-20">
          <h2 className="font-sans text-s20 font-w600 tracking-givmo text-text-strong">
            {identity.legal_name}
          </h2>
          <dl className="grid grid-cols-1 gap-16 sm:grid-cols-3">
            <Fact term="Kind of organization" value={words.organizationKind} />
            <Fact term="Chart of accounts" value={words.rulePack(identity.rule_pack_id)} />
            <Fact
              term="Fiscal year"
              value={`Ends in ${fiscalYearEndWords(identity.fiscal_year_end_month)}`}
            />
          </dl>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card testId="books-months">
          <CardHeader title="Months" />
          {years.length === 0 ? (
            <CardBody className="flex flex-col gap-8" testId="books-months-empty">
              <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                No months yet
              </p>
              <p className="max-w-[520px] font-sans text-s14 tracking-givmo text-text-secondary">
                These books have a chart of accounts and no entries yet.
              </p>
              <BooksLink
                view={{ page: 'ledger' }}
                className="mt-4 w-fit font-sans text-s14 font-w600 tracking-givmo text-brand-600 hover:underline"
              >
                See the chart of accounts
              </BooksLink>
            </CardBody>
          ) : (
            <div className="flex flex-col">
              {years.map((year, index) => (
                <FiscalYearGroup
                  key={year.fiscalYear}
                  year={year}
                  startOpen={index === 0}
                  words={words}
                  statement={firstRow && monthScope ? firstRow.statement : null}
                  monthScope={monthScope ?? null}
                />
              ))}
              {explain.length > 0 ? (
                <div className="flex flex-col gap-4 border-t border-border-subtle px-20 py-14">
                  {explain.map((status) => (
                    <p
                      key={status}
                      className="font-sans text-s12 tracking-givmo text-text-tertiary"
                    >
                      <span className="font-w600 text-text-secondary">
                        {words.status(status).label}:
                      </span>{' '}
                      {words.status(status).meaning}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </Card>

        <Card testId="books-read">
          <CardHeader title="Statements" />
          <CardBody className="flex flex-col px-12 py-8">
            {home.statements.map((row) => (
              <BooksLink
                key={row.statement}
                view={{ page: 'statements', statement: row.statement }}
                className="rounded-md px-8 py-8 font-sans text-s14 tracking-givmo text-text hover:bg-surface-hover hover:text-brand-600"
                data-testid={`books-home-statement-${row.statement}`}
              >
                {row.label}
              </BooksLink>
            ))}
          </CardBody>
          <div className="flex flex-col border-t border-border-subtle px-12 py-8">
            <BooksLink
              view={{ page: 'ledger' }}
              className="flex flex-col gap-2 rounded-md px-8 py-8 hover:bg-surface-hover"
              data-testid="books-home-ledger"
            >
              <span className="font-sans text-s14 tracking-givmo text-text">Ledger</span>
              <span className="font-sans text-s12 tracking-givmo text-text-tertiary">
                The chart of accounts and every entry
              </span>
            </BooksLink>
            <BooksLink
              view={{ page: 'reconciliation' }}
              className="flex flex-col gap-2 rounded-md px-8 py-8 hover:bg-surface-hover"
              data-testid="books-home-reconciliation"
            >
              <span className="font-sans text-s14 tracking-givmo text-text">Reconciliation</span>
              <span className="font-sans text-s12 tracking-givmo text-text-tertiary">
                What has been compared, month by month
              </span>
            </BooksLink>
          </div>
        </Card>
      </div>
    </BooksPageShell>
  );
}

function Fact({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex flex-col gap-4">
      <dt className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
        {term}
      </dt>
      <dd className="font-sans text-s14 tracking-givmo text-text">{value}</dd>
    </div>
  );
}

interface FiscalYear {
  fiscalYear: number;
  periods: BooksPeriod[];
  allSealed: boolean;
}

/** The ladder by the fiscal year the engine names each period in, the latest year and month first. */
function groupByFiscalYear(periods: BooksPeriod[]): FiscalYear[] {
  const byYear = new Map<number, BooksPeriod[]>();
  for (const period of periods) {
    byYear.set(period.fiscal_year, [...(byYear.get(period.fiscal_year) ?? []), period]);
  }
  return [...byYear.entries()]
    .sort(([x], [y]) => y - x)
    .map(([fiscalYear, rows]) => {
      const sorted = [...rows].sort((x, y) => y.period.localeCompare(x.period));
      return {
        fiscalYear,
        periods: sorted,
        allSealed: sorted.every((period) => period.status === SEALED),
      };
    });
}

function FiscalYearGroup({
  year,
  startOpen,
  words,
  statement,
  monthScope,
}: {
  year: FiscalYear;
  startOpen: boolean;
  words: BooksWords;
  statement: string | null;
  monthScope: string | null;
}) {
  // The latest year, and any year with a month not sealed, open; a year whose every month is sealed
  // folds to one line. Every year can be opened or folded.
  const [open, setOpen] = useState(startOpen || !year.allSealed);
  const earliest = year.periods[year.periods.length - 1]!.period;
  const latest = year.periods[0]!.period;
  return (
    <section
      className="border-b border-border-subtle last:border-b-0"
      data-testid={`books-fy-${year.fiscalYear}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-12 px-20 py-12 text-left hover:bg-surface-hover"
      >
        <span className="flex items-center gap-8">
          <Icon
            icon={open ? ChevronDown : ChevronRight}
            size={14}
            aria-hidden
            className="text-text-tertiary"
          />
          <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            Fiscal year {year.fiscalYear}
          </span>
        </span>
        {!open && year.allSealed ? (
          <span className="font-sans text-s13 tracking-givmo text-text-secondary">
            {spanName(earliest, latest)} · every month {words.status(SEALED).label.toLowerCase()}
          </span>
        ) : null}
      </button>
      {open ? (
        <ul className="flex flex-col pb-8">
          {year.periods.map((period) => (
            <li
              key={period.period}
              className="flex items-center justify-between gap-12 px-20 py-8 pl-44"
              data-testid={`books-month-${period.period}`}
            >
              {statement !== null && monthScope !== null ? (
                <BooksLink
                  view={{ page: 'statements', statement }}
                  search={{ scope: monthScope, period: period.period }}
                  className="font-sans text-s14 tracking-givmo text-text hover:text-brand-600 hover:underline"
                >
                  {periodName(period.period)}
                </BooksLink>
              ) : (
                <span className="font-sans text-s14 tracking-givmo text-text">
                  {periodName(period.period)}
                </span>
              )}
              <StatusLabel status={period.status} words={words} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
